import React from "react";
import { Pencil, Trash2, Info, Loader } from "lucide-react";
import SearchableSelect from "../SearchableSelect";

const getStatusBadge = (status) => {
  const s = String(status || "").toUpperCase();
  switch (s) {
    case "AVAILABLE":
    case "KOSONG":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
          Kosong
        </span>
      );
    case "RESERVED":
    case "RESERVASI":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200">
          Reservasi
        </span>
      );
    case "OCCUPIED":
    case "TERISI":
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-rose-50 text-rose-700 ring-1 ring-rose-200">
          Terisi
        </span>
      );
    case "INACTIVE":
    case "NONAKTIF":
    default:
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 ring-1 ring-slate-200">
          Nonaktif
        </span>
      );
  }
};

export default function DataTableTable({
  tables = [],
  loading = false,
  error = null,
  currentPage = 1,
  itemsPerPage = 10,
  totalEntries = 0,
  startEntry = 1,
  endEntry = 10,
  maxPages = 1,
  onPageChange,
  onItemsPerPageChange,
  onEdit,
  onDelete,
  onDetail,
}) {
  return (
    <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm mt-6 overflow-hidden">
      {loading && (
        <div className="px-6 py-12 flex items-center justify-center text-slate-500">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          Memuat data meja...
        </div>
      )}

      {error && !loading && (
        <div className="px-6 py-4 bg-rose-50 border-b border-rose-200 text-rose-700">
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-36">
                    Aksi
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-32">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Nama Meja
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Kapasitas
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Catatan / Detail
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tables.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-12 text-center text-slate-400 text-sm">
                      Belum ada data meja yang ditambahkan.
                    </td>
                  </tr>
                ) : (
                  tables.map((item) => {
                    const capacityText = `${item.capacity || 2} Orang`;
                    const statusUpper = String(item.status || "").toUpperCase();
                    let notesDisplay = item.notes || "-";

                    if (statusUpper === "RESERVED" || statusUpper === "RESERVASI") {
                      notesDisplay = `Reservasi oleh: ${item.reserved_by || "Pelanggan Umum"}${item.notes ? ` (${item.notes})` : ""}`;
                    } else if (statusUpper === "OCCUPIED" || statusUpper === "TERISI") {
                      notesDisplay = `Terisi oleh: ${item.reserved_by || "Pelanggan Umum"}${item.notes ? ` (${item.notes})` : ""}`;
                    }

                    return (
                      <tr
                        key={item.id}
                        className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => onEdit?.(item)}
                              className="w-8 h-8 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center transition-colors"
                              title="Edit Meja">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => onDelete?.(item)}
                              className="w-8 h-8 bg-rose-500 text-white rounded-lg hover:bg-rose-600 flex items-center justify-center transition-colors"
                              title="Hapus Meja">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => onDetail?.(item)}
                              className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors"
                              title="Detail Meja">
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(item.status)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-900 font-bold">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700 font-medium">
                          {capacityText}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {notesDisplay}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">
                Showing {totalEntries === 0 ? 0 : startEntry} to {Math.min(endEntry, totalEntries || tables.length)} of {totalEntries || tables.length}{" "}
                entries
              </span>
              {onItemsPerPageChange && (
                <SearchableSelect
                  options={[
                    { value: 10, label: "10" },
                    { value: 25, label: "25" },
                    { value: 50, label: "50" },
                    { value: 100, label: "100" },
                  ]}
                  value={itemsPerPage}
                  onChange={(value) => onItemsPerPageChange(value)}
                  placeholder="Items per page"
                  displayKey="label"
                  valueKey="value"
                  className="w-20"
                  searchable={false}
                  size="sm"
                />
              )}
            </div>

            {onPageChange && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => onPageChange(1)}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  ‹‹
                </button>
                <button
                  onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  ‹
                </button>

                {Array.from({ length: Math.min(3, maxPages) }, (_, i) => {
                  let p = currentPage;
                  if (currentPage === 1) p = 1;
                  else if (currentPage === maxPages) p = maxPages - 2;
                  else p = currentPage - 1;

                  if (maxPages < 3) p = 1;

                  const pageNum = p + i;
                  if (pageNum > maxPages || pageNum < 1) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? "bg-teal-600 text-white shadow-sm shadow-teal-600/20"
                          : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-100"
                      }`}>
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() =>
                    onPageChange(Math.min(maxPages, currentPage + 1))
                  }
                  disabled={currentPage === maxPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  ›
                </button>
                <button
                  onClick={() => onPageChange(maxPages)}
                  disabled={currentPage === maxPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  ››
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
